export type Role = {
  company: string;
  role: string;
  period: string;
  location: string;
  /** One line on what the job actually was. */
  scope: string;
  bullets: string[];
  tech: string[];
  /** Anchor into the matching case study, if there is one. */
  caseStudy?: string;
  /**
   * The two axes worth reading across the three roles at once: what shape of
   * team it was, and what kind of engineering it demanded. Same three jobs,
   * two different progressions.
   */
  arc: {
    team: string;
    discipline: string;
    /** One sentence on what that combination actually taught. */
    body: string;
  };
};

export const EXPERIENCE: Role[] = [
  {
    company: "Peng-O-Labs",
    role: "Co-Founder & Lead Engineer",
    period: "Apr 2024 to Present",
    location: "Philadelphia, PA",
    scope:
      "Sole engineer on Swarmhold, one of two developers on Pier Pals, plus everything a two-person studio needs that is not code.",
    arc: {
      team: "Two-person studio",
      discipline: "Real-time systems, end to end",
      body: "Owning an entire system end to end, where every constraint is measurable and there is nobody to hand the hard part to.",
    },
    bullets: [
      "Architected and built **Swarmhold** end to end in Unity 6 and C#: server-authoritative multiplayer on Photon Fusion 2, a custom flow-field navigation system, a three-tier replication model, and a GPU-instanced render path that took 1,000 concurrent enemies from 52 fps to 84 on identical hardware, and holds 84 fps at 4,000.",
      "Shipped **Pier Pals** to Steam Early Access in August 2026. Godot, GDScript, four-player Steam P2P, six locales, live in four months from first commit.",
      "Built the studio's telemetry and release infrastructure: crash and save-trouble reports with a crash-surviving outbox, the **Node.js** API that receives them (per-product keys, rate limits, ticket routing), gameplay telemetry ingested into **Postgres**, versioned build pipelines and Steamworks integration for both titles; shipped **pengolabs.com** in Next.js.",
      "Owned the non-engineering half as well: game design, economy balancing, art direction, store pages, trailers, localization, and coordination with contract artists.",
    ],
    tech: [
      "Unity 6",
      "C#",
      "Photon Fusion 2",
      "Burst / Jobs",
      "Godot",
      "GDScript",
      "Next.js",
      "TypeScript",
      "Steamworks",
    ],
    caseStudy: "swarmhold",
  },
  {
    company: "Qualtrics",
    role: "Software Engineer, Widgets & Data Visualization",
    period: "Jul 2022 to Feb 2024",
    location: "Seattle, WA",
    scope:
      "Client-facing analytics visualization for enterprise dashboards, plus the tooling and observability the team ran on.",
    arc: {
      team: "Enterprise org",
      discipline: "Full-stack, weighted frontend",
      body: "Working at scale, inside a codebase other teams live in, on timelines that depend on people I do not manage.",
    },
    bullets: [
      "Built interactive analytics widgets and data visualizations in **React**, **TypeScript** and **D3** for enterprise customers.",
      "Led a multi-quarter, cross-functional visualization from concept to launch across Product, UX and two partner engineering teams. Delivered on schedule, with a **tenfold increase in early-access adoption**.",
      "Implemented the image security controls that earned **FedRAMP** authorization for federal customers.",
      "Refactored the core visualization codebase to a single rendering path, cutting roughly **50% of the lines** while improving performance and maintainability.",
      "Led the observability migration from Sumo Logic to **Splunk**, building on-call alerting dashboards and team-wide widget performance metrics. Migrated and optimized high-visibility dashboards, reducing KTLO load by about 20%.",
      "Rebuilt the CI/CD pipelines around parallelized stages and dependency caching: **66% faster builds**, **50% shorter deployments**.",
    ],
    tech: [
      "React",
      "TypeScript",
      "JavaScript",
      "D3.js",
      "AngularJS",
      "Jenkins",
      "Splunk",
    ],
    caseStudy: "qualtrics",
  },
  {
    company: "CDM Smith / Trinnex",
    role: "Full-Stack Software Engineer",
    period: "Sep 2020 to Mar 2022",
    location: "Philadelphia, PA",
    scope:
      "Secure, data-driven web applications for government clients under compliance constraints.",
    arc: {
      team: "Small consultancy",
      discipline: "Full-stack, weighted backend",
      body: "Shipping where correctness is the requirement rather than a preference, and the auditor reviewing it is a real person.",
    },
    bullets: [
      "Built compliance-bound web applications for government clients in **Angular**, **Oracle APEX** and **PL/SQL**, with data integrity as the primary requirement.",
      "Wrote a client-facing **VBA** automation tool from scratch that replaced a manual workflow and improved data accuracy.",
      "Automated deployment workflows that had been manual, reducing release friction and delivery time.",
    ],
    tech: ["Angular", "Oracle APEX", "PL/SQL", "JavaScript", "VBA"],
  },
];

export const EDUCATION = [
  {
    institution: "Rutgers University",
    credential: "BA, Information Technology & Informatics. Minor in Korean",
    period: "2016 to 2020",
  },
];

/**
 * The synthesis. Three places produced three different things; this is the
 * answer to "why hire someone who has been building games for two years".
 */
export const THROUGH_LINE = {
  heading: "Three experiences, always client-facing",
  body: [
    "What never changed is who was on the other end. Everything I built went straight to somebody outside the company: a government auditor, an enterprise customer's dashboard, a player who paid for it on Steam. Nobody in that list absorbs a mistake on my behalf, and none of them are inclined to forgive a slow page or a dropped frame. Poor performance has nowhere to hide when the person feeling it is the customer.",
    "Optimization is the part I am most confident in, and the part I enjoy most. A frame budget, a bandwidth ceiling, a build that takes too long: constraints that cannot be argued with, where opinions lose to a profiler. The work I am proudest of is where the system came out simpler and faster at the same time, because the first version was solving the problem one item at a time.",
  ],
};
